import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { PageHeader } from "../../components/Shell";
import { api, formatApiError } from "../../lib/api";
import { cacheEvents, getCachedEvents, queueOfflineScan, getPendingScans } from "../../lib/idb";
import { syncOfflineScans } from "../../lib/sync";
import { toast } from "sonner";
import { Camera, CameraOff, RefreshCw, CheckCircle2, AlertCircle } from "lucide-react";

const QR_READER_ID = "qr-reader";

export default function Scanner() {
  const [params] = useSearchParams();
  const queryToken = params.get("token");

  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [scanning, setScanning] = useState(false);
  const [scannerError, setScannerError] = useState("");
  const [recent, setRecent] = useState([]); // { token, status, name, ts }
  const [pending, setPending] = useState(0);
  const [online, setOnline] = useState(navigator.onLine);

  // Single Html5Qrcode instance for the lifetime of the component.
  const html5Ref = useRef(null);
  // Re-entrancy guards: prevent double start / double stop races.
  const startingRef = useRef(false);
  const stoppingRef = useRef(false);
  // De-dupe same-token rapid re-scans.
  const lastTokenRef = useRef({ token: null, at: 0 });

  // Load events (network → fallback to cached)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/events");
        setEvents(data);
        await cacheEvents(data);
      } catch {
        const cached = await getCachedEvents();
        setEvents(cached);
      }
    })();
  }, []);

  // Connection + pending count
  useEffect(() => {
    const upd = () => setOnline(navigator.onLine);
    window.addEventListener("online", upd);
    window.addEventListener("offline", upd);
    const tick = async () => setPending((await getPendingScans()).length);
    tick();
    const id = setInterval(tick, 2000);
    return () => {
      window.removeEventListener("online", upd);
      window.removeEventListener("offline", upd);
      clearInterval(id);
    };
  }, []);

  // If user navigated here from a /scan/:token URL, prefill once an event is chosen.
  useEffect(() => {
    if (!queryToken || !eventId) return;
    handleToken(queryToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  // Initialize the Html5Qrcode instance ONCE per mount, bound to the stable
  // container div. Tear it down safely on unmount.
  useEffect(() => {
    const id = setTimeout(() => {
      if (!html5Ref.current && document.getElementById(QR_READER_ID)) {
        try {
          html5Ref.current = new Html5Qrcode(QR_READER_ID, /* verbose */ false);
        } catch (e) {
          setScannerError(e?.message || String(e));
        }
      }
    }, 0);

    return () => {
      clearTimeout(id);
      const instance = html5Ref.current;
      html5Ref.current = null;
      if (!instance) return;
      (async () => {
        try {
          if (instance.isScanning) {
            await instance.stop();
          }
        } catch (_) { /* ignore */ }
        try {
          await instance.clear();
        } catch (_) { /* ignore */ }
      })();
    };
  }, []);

  const start = async () => {
    if (!eventId) {
      toast.error("Pick an event first");
      return;
    }
    if (startingRef.current || scanning) return; // prevent double-start
    if (!html5Ref.current) {
      setScannerError("Scanner not ready yet — please try again.");
      return;
    }
    startingRef.current = true;
    setScannerError("");
    try {
      await html5Ref.current.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 260, height: 260 } },
        (decoded) => handleToken(decoded),
        () => { /* per-frame decode errors are noisy — ignore */ }
      );
      setScanning(true);
    } catch (e) {
      const msg = e?.message || String(e) || "Camera unavailable";
      setScannerError(msg);
      toast.error("Camera unavailable: " + msg);
      setScanning(false);
    } finally {
      startingRef.current = false;
    }
  };

  const stop = async () => {
    if (stoppingRef.current) return;
    stoppingRef.current = true;
    try {
      const instance = html5Ref.current;
      if (instance && instance.isScanning) {
        try {
          await instance.stop();
        } catch (_) { /* ignore */ }
      }
    } finally {
      setScanning(false);
      stoppingRef.current = false;
    }
  };

  // Extract token from a raw QR payload (URL or raw token).
  const extractToken = (raw) => {
    try {
      const u = new URL(raw);
      const parts = u.pathname.split("/").filter(Boolean);
      const idx = parts.indexOf("scan");
      if (idx >= 0 && parts[idx + 1]) return parts[idx + 1];
    } catch {}
    return raw.trim();
  };

  const handleToken = async (raw) => {
    const token = extractToken(raw);
    const now = Date.now();
    if (lastTokenRef.current.token === token && now - lastTokenRef.current.at < 2000) return;
    lastTokenRef.current = { token, at: now };

    const ts = new Date().toISOString();

    if (navigator.onLine) {
      try {
        const { data } = await api.post("/attendance/scan", { token, event_id: eventId, timestamp: ts });
        if (data.status === "recorded") {
          if (data.attendance_status === "late") {
            toast.warning(`Late · ${data.member_name}`, { description: data.member_code || "" });
          } else {
            toast.success(`On time · ${data.member_name}`, { description: data.member_code || "" });
          }
        } else if (data.status === "duplicate") {
          toast.warning(`Already recorded · ${data.member_name}`);
        } else if (data.status === "invalid_token") {
          toast.error("Invalid QR (member not in this org)");
        } else if (data.status === "invalid_event") {
          toast.error("Invalid event");
        }
        setRecent((r) => [{
          token,
          status: data.status,
          attendance_status: data.attendance_status,
          name: data.member_name || "—",
          code: data.member_code || "",
          ts,
        }, ...r].slice(0, 30));
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      }
    } else {
      const client_id = `${token}|${eventId}|${ts}`;
      await queueOfflineScan({ client_id, token, event_id: eventId, timestamp: ts });
      toast.message("Saved offline · will sync when online");
      setRecent((r) => [{ token, status: "queued", name: "(offline)", ts }, ...r].slice(0, 30));
    }
  };

  const triggerSync = async () => {
    const r = await syncOfflineScans();
    if (!r.ok && r.reason === "offline") return toast.error("Still offline");
    if (r.ok) toast.success(`Sync done · ${r.synced} new, ${r.duplicate} dup, ${r.failed} failed`);
    setPending((await getPendingScans()).length);
  };

  return (
    <div data-testid="scanner-page">
      <PageHeader eyebrow="Org Admin" title="Scanner" />

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Left: controls + camera */}
        <div className="lg:col-span-2">
          <div className="border border-[#D2D8D4] rounded-md bg-white">
            <div className="p-4 border-b border-[#E4E4E7] flex flex-wrap gap-3 items-end">
              <div className="flex-1 min-w-[200px]">
                <label className="text-xs font-bold uppercase tracking-widest">Event</label>
                <select
                  value={eventId}
                  onChange={(e) => setEventId(e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-[#D2D8D4] rounded-md bg-white"
                  data-testid="scanner-event-select"
                >
                  <option value="">Choose an event…</option>
                  {events.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.name}
                    </option>
                  ))}
                </select>
              </div>
              {!scanning ? (
                <button
                  onClick={start}
                  disabled={!eventId}
                  className="px-5 py-2.5 rounded-md bg-[#05126E] text-white font-semibold hover:bg-[#030B45] disabled:opacity-50 flex items-center gap-2 transition-colors"
                  data-testid="start-scan-btn"
                >
                  <Camera className="w-4 h-4" /> Start scanning
                </button>
              ) : (
                <button
                  onClick={stop}
                  className="px-5 py-2.5 rounded-md border border-[#D2D8D4] font-semibold hover:border-[#0f1a14] hover:bg-[#F4F6F5] flex items-center gap-2 transition-colors"
                  data-testid="stop-scan-btn"
                >
                  <CameraOff className="w-4 h-4" /> Stop
                </button>
              )}
            </div>

            <div className="relative bg-black min-h-[320px]">
              {/* Stable container owned entirely by html5-qrcode. NEVER put
                  React-rendered children inside this div — they will collide
                  with the library's DOM manipulation and cause
                  "removeChild" errors on unmount/rerender. */}
              <div id={QR_READER_ID} className="w-full" data-testid="qr-reader" />

              {/* Overlay shown while idle — sibling, not child of #qr-reader. */}
              {!scanning && !scannerError && (
                <div className="absolute inset-0 grid place-items-center pointer-events-none">
                  <div className="text-center text-white p-8">
                    <Camera className="w-12 h-12 mx-auto mb-3 opacity-60" />
                    <div className="font-bold uppercase tracking-widest text-sm">Camera idle</div>
                    <div className="text-xs text-zinc-400 mt-1">
                      Choose an event then start scanning.
                    </div>
                  </div>
                </div>
              )}

              {/* Visible error overlay (instead of white screen). */}
              {scannerError && (
                <div
                  className="absolute inset-0 grid place-items-center bg-black/85 p-6"
                  data-testid="scanner-error"
                >
                  <div className="max-w-sm w-full bg-white rounded-md border border-[#E3E6E4] p-5 text-center">
                    <AlertCircle className="w-8 h-8 mx-auto text-[#B91C1C] mb-2" />
                    <div className="font-bold text-[#0f1a14]">Camera couldn’t start</div>
                    <div className="text-xs text-[#5a6663] mt-1 break-words">
                      {scannerError}
                    </div>
                    <div className="mt-4 flex gap-2 justify-center">
                      <button
                        onClick={() => { setScannerError(""); start(); }}
                        className="px-4 py-2 rounded-md bg-[#05126E] text-white font-semibold hover:bg-[#030B45] transition-colors text-sm"
                        data-testid="scanner-retry-btn"
                      >
                        Retry
                      </button>
                      <button
                        onClick={() => setScannerError("")}
                        className="px-4 py-2 rounded-md border border-[#D2D8D4] font-semibold hover:border-[#0f1a14] transition-colors text-sm"
                        data-testid="scanner-error-dismiss"
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="p-3 border-t border-[#E4E4E7] flex justify-between items-center text-xs">
              <div className={`font-bold uppercase tracking-widest ${online ? "text-[#05126E]" : "text-[#B91C1C]"}`}>
                {online ? "● ONLINE" : "● OFFLINE"}
              </div>
              <button
                onClick={triggerSync}
                className="px-3 py-1.5 rounded-md border border-[#D2D8D4] font-semibold hover:border-[#0f1a14] hover:bg-[#F4F6F5] flex items-center gap-2 transition-colors"
                data-testid="sync-now-btn"
              >
                <RefreshCw className="w-3 h-3" /> Sync now {pending > 0 && `(${pending})`}
              </button>
            </div>
          </div>
        </div>

        {/* Right: recent scans + pending */}
        <div>
          <div className="border border-[#E4E4E7] bg-white">
            <div className="px-4 py-3 border-b border-[#E4E4E7] flex justify-between items-center">
              <div className="text-[10px] font-bold uppercase tracking-[0.25em]">Recent scans</div>
              <div className="text-xs mono">{recent.length}</div>
            </div>
            <div className="max-h-[420px] overflow-y-auto">
              {recent.length === 0 && (
                <div className="p-6 text-center text-xs text-zinc-500">No scans yet</div>
              )}
              {recent.map((r, i) => (
                <div key={i} className="px-4 py-3 border-b border-[#E4E4E7] flex items-center gap-3 text-sm">
                  {r.status === "recorded" || r.status === "queued" ? (
                    <CheckCircle2 className="w-4 h-4 text-[#05126E]" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-[#B91C1C]" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{r.name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex gap-2 items-center">
                      <span>{r.code}</span>
                      {r.attendance_status === "late" && (
                        <span className="px-1.5 py-0.5 rounded bg-[#FFF3CD] text-[#7a5b00] border border-[#C9A227]/40 normal-case tracking-normal">
                          Late
                        </span>
                      )}
                      {r.attendance_status === "on_time" && (
                        <span className="px-1.5 py-0.5 rounded bg-[#E6E9F5] text-[#05126E] border border-[#05126E]/30 normal-case tracking-normal">
                          On time
                        </span>
                      )}
                      <span>· {new Date(r.ts).toLocaleTimeString()}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {pending > 0 && (
            <div className="mt-4 border-2 border-[#C9A227] bg-[#FFF8E1] p-4" data-testid="pending-banner">
              <div className="text-[10px] font-bold uppercase tracking-[0.25em]">Pending sync</div>
              <div className="text-3xl font-black">{pending}</div>
              <div className="text-xs text-zinc-700 mt-1">
                Will sync automatically when online.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
